export const ROBUST_ASSESSMENT_SCHEMA_VERSION = 5;
export const ROBUST_ASSESSMENT_MODEL = "nervous-system-baseline-v5";

export const UNKNOWN_VALUE = "unknown";
export const NOT_APPLICABLE_VALUE = "not-applicable";

export const RECENT_CHANGE_OPTIONS = [
  { key: "none", label: "No clear recent change" },
  { key: "harder", label: "My nervous-system reaction has become stronger or harder to manage" },
  { key: "easier", label: "My nervous-system reaction has become less intense or easier to manage" },
  { key: "mixed", label: "Some things improved while others became harder" },
  { key: UNKNOWN_VALUE, label: "I am not sure" },
];

export const RECENT_PATTERN_OPTIONS = [
  "more alert, tense, or physically activated",
  "more angry or irritable",
  "more anxious or overwhelmed",
  "more restless or unable to settle",
  "lower physical energy or greater depletion",
  "more sad or emotionally low",
  "more numb, withdrawn, or disengaged",
  "more difficulty focusing or shifting attention",
  "slower recovery after stress or activity",
  "more calm or steady",
  "more positive or energized",
  "another change",
  "not sure",
];

export const CHANGE_TIMING_OPTIONS = [
  { key: "two-weeks", label: "Within the past two weeks" },
  { key: "two-four-weeks", label: "Two to four weeks ago" },
  { key: "one-three-months", label: "One to three months ago" },
  { key: "three-twelve-months", label: "Three to twelve months ago" },
  { key: "over-year", label: "More than a year ago" },
  { key: UNKNOWN_VALUE, label: "Not sure" },
];

export const HELPFUL_FACTOR_OPTIONS = [
  "walking or easy movement",
  "aerobic exercise",
  "strength training",
  "sport or recreation",
  "stretching or mobility",
  "breathing or somatic practice",
  "1:1 coaching",
  "counselling or therapy",
  "group class or team activity",
  "connection with a partner, friend, or family member",
  "time alone or in a quieter setting",
  "outdoor time or natural light",
  "structured routine",
  "improved sleep or rest",
  "hydration or nourishment",
  "reducing screens, notifications, or sensory load",
  "changing locations or environments",
  "medical or other professional support",
  "nothing clearly helped",
  "not sure",
  "other",
];

export const WORSENING_FACTOR_OPTIONS = [
  "poor or disrupted sleep",
  "missed meals, low intake, or low hydration",
  "inactivity or too little movement",
  "excessive, unfamiliar, or poorly recovered exercise",
  "heavy work, school, or caregiving demand",
  "interpersonal conflict",
  "being watched, compared, or evaluated",
  "competition or performance pressure",
  "too much time alone",
  "crowding, noise, screens, or interruptions",
  "unpredictable schedules",
  "travel or transitions between locations",
  "pain, illness, or physical limitation",
  "alcohol, substance, or medication effects",
  "several demands occurring at once",
  "nothing clearly made it worse",
  "not sure",
  "other",
];

export const EMOTION_OPTIONS = [
  "angry or irritable",
  "anxious or overwhelmed",
  "restless or activated",
  "sad or low",
  "numb or withdrawn",
  "depleted",
  "positive or excited",
  "calm or steady",
  "mixed or difficult to describe",
  "no consistent feeling",
  "another feeling",
];

export const BODY_SIGNAL_OPTIONS = [
  "breathing changes",
  "heart or pulse becomes noticeable",
  "muscle tension",
  "heat or sweating",
  "restlessness or pacing",
  "shakiness",
  "heaviness",
  "stomach or digestive changes",
  "pain becomes stronger",
  "sensitivity to noise, light, or touch",
  "fatigue",
  "no clear physical change",
  "other",
];

export const COGNITIVE_SIGNAL_OPTIONS = [
  "racing thoughts",
  "tunnel vision",
  "repetitive thoughts",
  "difficulty shifting attention",
  "difficulty switching tasks",
  "mental fog",
  "indecision",
  "hyperfocus",
  "losing track of what I was doing",
  "no clear cognitive change",
  "other",
];

export const REACTION_DIRECTION_OPTIONS = [
  "move or take action",
  "solve or focus on the problem",
  "confront or approach",
  "leave or create space",
  "withdraw or disengage",
  "seek connection or support",
  "seek stimulation or distraction",
  "freeze, stop, or become still",
  "continue functioning while feeling activated internally",
  "mixed or uncertain",
];

export const DIFFICULT_ENVIRONMENT_OPTIONS = [
  "noisy environments",
  "bright or changing light",
  "crowded environments or limited personal space",
  "screens and notifications",
  "cluttered or visually busy environments",
  "hot, cold, or uncomfortable temperatures",
  "frequent interruptions",
  "lack of privacy",
  "unpredictable schedules",
  "travel or commuting",
  "transitions between work, home, or other locations",
  "particular places associated with difficult experiences or patterns",
  "none of these",
  "not sure",
  "other",
];

export const HELPFUL_ENVIRONMENT_OPTIONS = [
  "quiet or private space",
  "outdoor space or natural light",
  "familiar environment",
  "predictable or structured environment",
  "open space",
  "movement or exercise setting",
  "reduced-screen environment",
  "music or creative environment",
  "another setting",
  "no consistent setting",
  "not sure",
];

export const SOCIAL_SETTING_OPTIONS = [
  "alone",
  "with a close friend, partner, or family member",
  "1:1 coaching",
  "small group",
  "group class or team activity",
  "larger group",
  "it depends on the situation",
  "no consistent preference",
  "not sure",
];

export const RECOVERY_DURATION_OPTIONS = [
  { key: "minutes", label: "Within a few minutes", score: 5 },
  { key: "hour", label: "Within 15–60 minutes", score: 4 },
  { key: "hours", label: "A few hours", score: 3 },
  { key: "day", label: "Most of the day", score: 2 },
  { key: "next-day", label: "Until the next day or longer", score: 1 },
  { key: "variable", label: "It varies too much to say", score: null },
  { key: UNKNOWN_VALUE, label: "Not sure", score: null },
];

export const CONFLICT_CARRYOVER_OPTIONS = [
  { key: "minutes", label: "It settles within minutes", score: 5 },
  { key: "hour", label: "It remains active for up to an hour", score: 4 },
  { key: "hours", label: "It remains active for several hours", score: 3 },
  { key: "day", label: "It remains active for most of the day", score: 2 },
  { key: "next-day", label: "It carries into the next day or longer", score: 1 },
  { key: "variable", label: "It varies significantly", score: null },
  { key: UNKNOWN_VALUE, label: "Not sure", score: null },
];

export const ACTIVITY_CATEGORY_OPTIONS = [
  "walking or easy movement",
  "aerobic or cardio exercise",
  "strength training",
  "sport or recreation",
  "stretching or mobility",
  "group classes or team activity",
  "1:1 coaching",
  "physiotherapy or rehabilitation",
  "no regular activity right now",
  "other",
];

export const MOVEMENT_DAYS_OPTIONS = [
  { key: "zero", label: "0 days", score: 1 },
  { key: "one-two", label: "1–2 days", score: 2 },
  { key: "three-four", label: "3–4 days", score: 3 },
  { key: "five-six", label: "5–6 days", score: 4 },
  { key: "daily", label: "Daily or almost daily", score: 5 },
  { key: UNKNOWN_VALUE, label: "Not sure", score: null },
];

export const CORE_SCALE_SECTIONS = [
  {
    id: "mobilization",
    label: "02 · Detecting and mobilizing",
    heading: "How does your nervous system begin responding?",
    body: "Think about your typical nervous-system reaction over the past few months—not only your best or worst day.",
    questions: [
      {
        key: "stateDetection",
        q: "How early do you usually notice that your nervous-system state is changing?",
        low: "1 · Usually after it affects what I do",
        high: "5 · Usually before it affects what I do",
      },
      {
        key: "interoceptiveClarity",
        q: "How clearly can you identify the first physical change in your body?",
        low: "1 · Hard to identify",
        high: "5 · Usually clear",
      },
      {
        key: "mobilizationSensitivity",
        q: "How easily does your body shift into high-alert or action mode?",
        low: "1 · Usually requires a major demand",
        high: "5 · Small demands can activate it",
      },
      {
        key: "mobilizationMagnitude",
        q: "When that nervous-system reaction starts, how physically strong does it usually become?",
        low: "1 · Mild",
        high: "5 · Very strong",
      },
      {
        key: "availableEnergy",
        q: "When the nervous-system reaction is strong, how much usable physical energy do you usually have?",
        low: "1 · Very depleted",
        high: "5 · Strong usable energy",
      },
      {
        key: "actionPressure",
        q: "How strong is the pressure to act, move, avoid, argue, leave, shut down, or do something immediately?",
        low: "1 · Little pressure",
        high: "5 · Very strong pressure",
      },
    ],
  },
  {
    id: "control",
    label: "03 · Control, attention, and recovery",
    heading: "How much control remains, and how does recovery happen?",
    body: "Here, reaction means the physical, emotional, and mental response that shows up together—not only something another person can see.",
    questions: [
      {
        key: "behavioralControl",
        q: "Even when your reaction is strong, how much control do you still have over what you do next?",
        low: "1 · It is all reaction",
        high: "5 · I am still in control",
      },
      {
        key: "stopChangeControl",
        q: "Once a reaction has started, how easily can you slow it down, change direction, or stop?",
        low: "1 · Very difficult",
        high: "5 · I can reliably change or stop",
      },
      {
        key: "cognitiveCapture",
        q: "When something strongly affects you, how difficult is it to shift your attention away from it?",
        low: "1 · Easy to redirect",
        high: "5 · Very difficult",
        demandDirection: true,
      },
      {
        key: "mobilizationCapacity",
        q: "When action is needed, how easily can you build enough energy and focus to act?",
        low: "1 · Hard to mobilize",
        high: "5 · I can mobilize reliably",
      },
      {
        key: "recoveryInitiation",
        q: "Once the demand is over, how easily does your body begin recovering?",
        low: "1 · The reaction stays active",
        high: "5 · Recovery begins reliably",
      },
    ],
  },
  {
    id: "capacity",
    label: "07 · Physical capacity",
    heading: "What can your body competently and recoverably do?",
    body: "These answers help APOS determine a relative capacity ceiling without treating age, size, or appearance as substitutes for demonstrated ability.",
    questions: [
      {
        key: "goalSupport",
        q: "How well does your current activity level support the physical goals you are working toward?",
        low: "1 · Not at all",
        high: "5 · Very well",
      },
      {
        key: "movementFreedom",
        q: "How free are you from pain, injury, or movement limitations during familiar activity?",
        low: "1 · Very limited",
        high: "5 · Fully unrestricted",
      },
      {
        key: "movementControl",
        q: "How confident are you controlling, slowing, or stopping familiar movement when needed?",
        low: "1 · Low control",
        high: "5 · Strong control",
      },
      {
        key: "trainingTolerance",
        q: "How well do you tolerate and recover from your current exercise or sport volume?",
        low: "1 · Poorly",
        high: "5 · Very well",
      },
      {
        key: "activityFamiliarity",
        q: "How familiar and technically comfortable are you with the activities you currently perform?",
        low: "1 · Mostly unfamiliar",
        high: "5 · Highly familiar",
      },
      {
        key: "recentLoadManageability",
        q: "How manageable has your recent physical training, work, and activity load felt?",
        low: "1 · Often exceeds recovery",
        high: "5 · Usually manageable",
      },
    ],
  },
  {
    id: "resources",
    label: "08 · Recovery resources",
    heading: "What supports usable energy and recovery?",
    body: "These variables modify capacity and recovery. They do not define your nervous-system state by themselves.",
    questions: [
      {
        key: "sleepRestoration",
        q: "How restorative does your sleep usually feel?",
        low: "1 · Not restorative",
        high: "5 · Reliably restorative",
      },
      {
        key: "sleepConsistency",
        q: "How consistent is your sleep timing?",
        low: "1 · Very irregular",
        high: "5 · Consistent",
      },
      {
        key: "nourishmentResource",
        q: "How consistently do you get enough nourishment to support energy and recovery?",
        low: "1 · Often insufficient",
        high: "5 · Reliable",
      },
      {
        key: "hydrationResource",
        q: "How consistently do you drink enough to support energy and recovery?",
        low: "1 · Often low",
        high: "5 · Reliable",
      },
      {
        key: "recoveryOpportunity",
        q: "How much lower-demand time do you usually have to rest and recover?",
        low: "1 · Very limited",
        high: "5 · Enough",
      },
      {
        key: "routineFit",
        q: "How well does your usual routine support your responsibilities, activity, and recovery?",
        low: "1 · Usually works against them",
        high: "5 · Usually supports them",
      },
    ],
  },
];

export const ROBUST_SECTION_ORDER = [
  "recent",
  "mobilization",
  "control",
  "expression",
  "environment",
  "connection",
  "capacity",
  "resources",
  "activity",
];

export const ROBUST_SECTION_LABELS = {
  recent: "01 · Recent change",
  mobilization: "02 · Detecting and mobilizing",
  control: "03 · Control, attention, and recovery",
  expression: "04 · How the nervous-system state appears",
  environment: "05 · Environment",
  connection: "06 · Connection and social context",
  capacity: "07 · Physical capacity",
  resources: "08 · Recovery resources",
  activity: "09 · Activity and recreation",
};
